// The switch-panel control screen — one component covering the four Flutter
// screens that share this design:
//
//   oneTouchScreen   1 relay,  single centred tile
//   twoTouchScreen   2 relays, one row, taller tiles (height = 1.5 × tile)
//   fourTouchScreen  4 relays, 2×2 laid out in the order [0, 2, 1, 3]
//   switchFanScreen  DeviceID 92 (4+1) and 93 (7+1) — the switch bank
//
// Sizing follows the Dart exactly: derive ONE `tile` unit that fits within both
// the available width and height, clamp it to 120–230, and express the icon,
// font and All-bars relative to it. That is why a tablet doesn't blow the
// buttons up and landscape doesn't clip the stack.
//
// The 4-relay layout keeps Flutter's customOrder [0, 2, 1, 3] — the physical
// button order on the hardware, which is NOT left-to-right.

import React, { useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RelayTile from './components/RelayTile';
import AllBar from './components/AllBar';
import DeviceAppBar from './components/DeviceAppBar';
import { useApp } from '../../../../core/state/AppContext';
import { useRelayStatus } from '../../../../core/hooks/useRelayStatus';
import { getOutputs } from '../../../../core/api/api';
import { loadButtonSettings, marksFor } from '../../../../core/devices/buttonStatus';
// Shared with the desktop card — this screen used to hold its own copy of the
// order, which is how the two shells came to disagree.
import { displayOrder } from '../../../../core/devices/buttonLayout';

export default function TouchScreen({ device, relayCount, showAllBars = true, tall = false }) {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId } = useApp();

  const uno = device?.UniqueNumber;
  const { states, known, toggleRelay, setAll } = useRelayStatus(uno, relayCount, userId);

  const [names, setNames] = useState(() => new Array(relayCount).fill(''));
  const [outputRows, setOutputRows] = useState([]);
  const [settings, setSettings] = useState(null); // { schedulesByOutput, mappings }

  // Output names AND the auto-off column — OnTimeAfterOn rides along in the same
  // rows, so the timer mark costs no extra request.
  useEffect(() => {
    if (!uno) return undefined;
    let alive = true;
    getOutputs(uno)
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        if (!alive) return;
        setOutputRows(rows);
        setNames(Array.from({ length: relayCount },
          (_, i) => rows[i]?.OutputName ?? 'Unnamed'));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [uno, relayCount]);

  // Button settings: schedules per output (which also drives the clock badge,
  // as Flutter's _isScheduled did) plus the two-way mappings for the device.
  useEffect(() => {
    if (!uno) return undefined;
    let alive = true;
    loadButtonSettings(uno, relayCount)
      .then((s) => { if (alive) setSettings(s); })
      .catch(() => {});
    return () => { alive = false; };
  }, [uno, relayCount]);

  // Re-evaluate "pending today" on a timer so a schedule's time disappears once
  // it passes, without refetching anything — the rows are already in memory and
  // only the comparison against now changes.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const marks = useMemo(() => {
    if (!settings) return [];
    const weekday = now.getDay();               // 0 = Sunday, matching Weekly
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return Array.from({ length: relayCount }, (_, i) => marksFor({
      output: i + 1,
      schedulesByOutput: settings.schedulesByOutput,
      mappings: settings.mappings,
      outputRow: outputRows[i],
      uniqueNumber: uno,
      weekday,
      nowMinutes,
    }));
  }, [settings, outputRows, uno, relayCount, now]);

  // ── Responsive sizing (fourTouchScreen.dart) ──────────────────────────────
  const geom = useMemo(() => {
    const availW = window.innerWidth;
    const availH = window.innerHeight - 64; // AppBar
    const rows = relayCount <= 1 ? 1 : relayCount <= 2 ? 1 : Math.ceil(relayCount / 2);
    const heightDivisor = showAllBars ? rows + 2 : rows;
    let tile = Math.min((availW - 40) / 2, (availH - 90) / Math.max(2, heightDivisor));
    tile = Math.min(230, Math.max(120, tile));
    return {
      tile,
      tileHeight: tall ? tile * 1.5 : tile,
      iconSize: tile * 0.26,
      nameFont: Math.min(24, Math.max(14, tile * 0.13)),
      barWidth: tile * 2 + 40,
      barHeight: tile * 0.9,
      barFont: Math.min(30, Math.max(18, tile * 0.16)),
      barPad: tile * 0.12,
    };
  }, [relayCount, showAllBars, tall]);

  const order = displayOrder(relayCount);

  const openSchedule = (i) => navigate(`/schedule/${uno}/${i}`, {
    state: { outputName: names[i], device },
  });

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} />

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        py: 2,
        overflowY: 'auto',
      }}
      >
        {showAllBars && (
          <AllBar
            variant="off"
            onClick={() => setAll(false)}
            width={geom.barWidth}
            height={geom.barHeight}
            fontSize={geom.barFont}
            padding={geom.barPad}
          />
        )}

        <Box sx={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center', maxWidth: geom.barWidth,
        }}
        >
          {order.map((idx) => (
            <Box key={idx} sx={{ p: '10px' }}>
              <RelayTile
                name={names[idx] || ''}
                isOn={!!states[idx]}
                isKnown={!!known[idx]}
                marks={marks[idx]}
                onToggle={() => toggleRelay(idx)}
                onSchedule={() => openSchedule(idx)}
                width={geom.tile}
                height={geom.tileHeight}
                iconSize={geom.iconSize}
                nameFont={geom.nameFont}
              />
            </Box>
          ))}
        </Box>

        {showAllBars && (
          <AllBar
            variant="on"
            onClick={() => setAll(true)}
            width={geom.barWidth}
            height={geom.barHeight}
            fontSize={geom.barFont}
            padding={geom.barPad}
          />
        )}
      </Box>
    </Box>
  );
}
